<template>
  <section class="smart-hotel-search" :class="{ 'awaiting-search': !hasSearched }" :aria-busy="isSearching"
           aria-label="AI搜酒店">
    <div class="search-panel">
      <div class="toolbar">
        <div class="region-tabs" role="group" aria-label="酒店地区">
          <button v-for="item in regions" :key="item.id" type="button" :class="{ active: region === item.id }"
                  :aria-pressed="region === item.id" :disabled="isSearching" @click="changeRegion(item.id)">
            {{ item.label }}
          </button>
        </div>
        <SearchModeSwitch :smart="true" @change="$emit('change-mode', $event)"/>
      </div>
      <form class="query-bar" @submit.prevent="search">
        <span class="ai-icon" ><i class="iconfont icon-ai-stars" style="font-size: 14px;position: relative;top: -2px"/></span>
        <label class="query-field"><span>AI搜酒店</span>
          <el-input ref="queryInput" v-model="query" class="query-input" type="textarea"
                    :autosize="{ minRows: 1, maxRows: 4 }" :readonly="isSearching" :maxlength="1000"
                    aria-label="酒店需求" @keydown.native="handleQueryKeydown"
                    placeholder="说出地点、日期、人数、预算或偏好，一句话即可搜索"/>
          <div class="query-input-meta">
            <span>Enter 搜索 · Ctrl + Enter 换行</span>
            <span>{{ query.length }}/1000</span>
          </div>
        </label>
        <button v-if="query && !isSearching" class="clear-query-button" type="button"
                aria-label="清空酒店需求" title="清空" @click="clearQuery">
          <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true" focusable="false">
            <circle cx="8" cy="8" r="6.5" fill="none" stroke="currentColor" stroke-width="1"/>
            <path d="M6 6l4 4m0-4l-4 4" stroke="currentColor" stroke-width="1" stroke-linecap="round"/>
          </svg>
        </button>
        <button class="search-button" :class="{ 'is-searching': isSearching }" :disabled="isSearching" type="submit">
          <span v-if="isSearching" class="loading-sparkle" aria-hidden="true">
            <i class="iconfont icon-ai-stars" style="font-size: 18px;position: relative;top: -6px"/>
          </span>
          <i v-else class="iconfont icon-search" aria-hidden="true" style="font-size: 18px;position: relative;top: -3px;margin-right: 5px"></i>
          {{ isSearching ? 'AI搜索中' : 'AI搜酒店' }}
        </button>
      </form>
      <div v-if="isSearching" class="search-progress" role="status" aria-live="polite">
        <span class="progress-spinner" aria-hidden="true"></span>
        <span>{{ loadingMessages[loadingMessageIndex] }}</span>
      </div>
      <div v-else-if="!hasSearched" class="query-examples" aria-label="示例需求">
        <span>试试这样说</span>
        <button v-for="example in examples" :key="example" type="button" :title="example"
                @click="selectExample(example)">{{ example }}
        </button>
      </div>
    </div>

    <div v-if="hasSearched && !isSearching" class="results-panel" aria-live="polite">
      <div v-if="hotels.length" class="results-heading">
        <strong>已按你的需求找到酒店</strong><span class="result-count">共 {{ hotels.length }} 家</span>
        <div class="requirement-tags">
          <span v-for="tag in requirementTags" :key="tag">
            <i class="el-icon-success" />
            {{ tag }}
          </span>
        </div>
        <div class="requirement-tags not">
          <span v-for="tag in notMappingRequirementTags" :key="tag">
            <i class="el-icon-info" />
            {{ tag }}
          </span>
        </div>
      </div>
      <div v-if="hotels.length" class="hotel-grid">
        <HotelCard v-for="(hotel, index) in hotels" :key="index" :hotel="hotel" :landmark="requirements.landmark"
                   @book="toBookingDetail" @toHotelDetail="toHotelDetail"/>
      </div>
      <NoHotelResults v-else :tags="recognizedTags" :location="requirements.landmark || ''" :tips="tips"/>
    </div>

    <el-dialog :visible.sync="isShowReturn" :show-close="false" :center="true" :close-on-click-modal="false"
               :close-on-press-escape="false" width="500px" custom-class="smart-hotel-booking-dialog" append-to-body>
      <div style="text-align: center; font-size: 18px">
        <div>手慢了，请返回重新选择购买！</div>
        <div class="btn-normal">
          <el-button type="primary" @click="isShowReturn = false" style="margin-top: 30px; width: 100%">
            确定
          </el-button>
        </div>
      </div>
    </el-dialog>

    <el-dialog :visible.sync="isShowContinue" :show-close="false" :center="true" :close-on-click-modal="false"
               :close-on-press-escape="false" width="500px" custom-class="smart-hotel-booking-dialog" append-to-body>
      <div style="font-size: 18px; line-height: 25px">
        <div>酒店价格发生变化，每日均价升高{{ upPrice }}，是否继续预订？</div>
        <div style="text-align: center; margin-top: 30px">
          <el-button @click="isShowContinue = false" style="width: 200px">
            返回
          </el-button>
          <el-button type="warning" @click="toDetail" style="width: 200px">
            继续预订
          </el-button>
        </div>
      </div>
    </el-dialog>
  </section>
</template>

<script>
import SearchModeSwitch from './SearchModeSwitch.vue'
import HotelCard from './HotelCard.vue'
import NoHotelResults from './NoHotelResults.vue'

const defaults = (location, checkIn, nights, rooms, adults, star, tags, minPricePerNight, maxPricePerNight) => ({
  landmark: location,
  date: checkIn,
  nights,
  rooms,
  adults,
  star,
  tags,
  minPricePerNight,
  maxPricePerNight
})

export default {
  name: 'SmartHotelSearch',
  components: {SearchModeSwitch, HotelCard, NoHotelResults},
  data () {
    return {
      regions: [{id: 1, label: '国内'}, {id: 2, label: '海外'}],
      region: 1,
      query: '',
      submittedQuery: '',
      hasSearched: false,
      isSearching: false,
      loadingMessageIndex: 0,
      loadingTimer: null,
      loadingMessages: ['正在理解位置、日期、人数与预算…', 'AI 正在匹配符合条件的酒店…', '正在查询可售房型与实时价格，请稍候…'],
      requirements: {},
      notMappingRequirements: {},
      hotels: [],
      isShowReturn: false,
      isShowContinue: false,
      upPrice: 0,
      actualSearchCondition: {},
      parsedSearchCondition: {},
      checkRateReq: {},
      checkRateInfo: {},
      hotelInfoBriefly: {},
      currentRoom: {},
      bookingInfo: {},
      tips: []
    }
  },
  computed: {
    examples () {
      return this.region === 1
        ? ['上海虹桥站附近，明天住一晚', '深圳湾口岸附近，适合商务接待', '下周末香港亲子酒店，要有泳池']
        : ['曼谷河畔，明天住一晚', '东京新宿附近，适合商务出行', '下周末新加坡亲子酒店，要有泳池']
    },
    recognizedTags () {
      const r = this.requirements
      const tags = []
      if (r.landmark) tags.push(r.landmark + '附近')
      if (typeof r.date === 'string' && /^\d{4}-\d{2}-\d{2}/.test(r.date)) {
        const date = r.date.slice(5, 10).split('-')
        tags.push(date[0] + '月' + date[1] + '日' + (r.nights ? ' · ' + r.nights + '晚' : ''))
      }
      if (r.rooms && r.adults) tags.push(r.rooms + '间 · ' + r.adults + '成人')
      if (r.star) tags.push(r.star)
      if (r.minPricePerNight) tags.push(`${r.minPricePerNight}元以上`)
      if (r.maxPricePerNight) tags.push(`${r.maxPricePerNight}元以内`)
      if (r.tags && r.tags.length) {
        tags.push(...r.tags)
      }
      return tags
    },
    recognizedNotMapTags () {
      const r = this.notMappingRequirements
      const tags = []
      if (r.landmark) tags.push(r.landmark + '附近')
      if (typeof r.date === 'string' && /^\d{4}-\d{2}-\d{2}/.test(r.date)) {
        const date = r.date.slice(5, 10).split('-')
        tags.push(date[0] + '月' + date[1] + '日' + (r.nights ? ' · ' + r.nights + '晚' : ''))
      }
      if (r.rooms && r.adults) tags.push(r.rooms + '间 · ' + r.adults + '成人')
      if (r.star) tags.push(r.star)
      if (r.minPricePerNight) tags.push(`${r.minPricePerNight}元以上`)
      if (r.maxPricePerNight) tags.push(`${r.maxPricePerNight}元以内`)
      if (r.tags && r.tags.length) {
        tags.push(...r.tags)
      }
      return tags
    },
    requirementTags () {
      return this.recognizedTags.slice()
    },
    notMappingRequirementTags () {
      return this.recognizedNotMapTags.slice()
    }
  },
  beforeDestroy () {
    clearInterval(this.loadingTimer)
  },
  created () {
    if (this.$common.getAccountType() === 3) {
      this.regions = [{id: 1, label: '国内'}, {id: 2, label: '海外'}]
      this.region = 1
    } else if (this.$common.getAccountType() === 1) {
      this.regions = []
      this.region = 1
    } else if (this.$common.getAccountType() === 2) {
      this.regions = []
      this.region = 2
    }
  },
  methods: {
    handleQueryKeydown (event) {
      if (event.key !== 'Enter' || event.isComposing || event.keyCode === 229 || event.target.composing) return
      event.preventDefault()
      if (this.isSearching) return
      if (!event.ctrlKey) {
        this.search()
        return
      }
      const input = event.target
      const start = input.selectionStart
      const end = input.selectionEnd
      if (this.query.length - (end - start) >= 1000) return
      this.query = this.query.slice(0, start) + '\n' + this.query.slice(end)
      this.$nextTick(() => {
        input.setSelectionRange(start + 1, start + 1)
        input.scrollTop = input.scrollHeight
      })
    },
    clearQuery () {
      this.query = ''
      this.$refs.queryInput.focus()
    },
    changeRegion (region) {
      if (this.region === region) return
      this.region = region
      this.submittedQuery = ''
      this.query = ''
      this.hasSearched = false
      this.$emit('search-state-change', false)
      this.booking = null
    },
    selectExample (example) {
      this.query = example
      this.$refs.queryInput.focus()
    },
    async search () {
      if (this.isSearching) return
      if (!this.$commonUtil.isLogin()) {
        this.$message.warning('请先登录')
        this.$router.push('/login')
        return
      }
      if (!this.query.trim()) {
        this.$refs.queryInput.focus()
        return
      }
      this.isSearching = true
      this.loadingMessageIndex = 0
      this.loadingTimer = setInterval(() => {
        this.loadingMessageIndex = (this.loadingMessageIndex + 1) % this.loadingMessages.length
      }, 2000)
      try {
        this.actualSearchCondition = {}
        this.parsedSearchCondition = {}
        this.checkRateReq = {}
        this.checkRateInfo = {}
        this.currentRoom = {}
        this.bookingInfo = {}
        this.tips = []

        this.submittedQuery = this.query.trim()
        let headers = {accessToken: this.$common.isLogin() ? JSON.parse(this.$common.isLogin()).token : ''}
        const {data: result} = await this.$http.post('/mini/aiSearchHotels', {content: this.submittedQuery, regionId: this.region}, {headers})
        if (result.code !== 200) {
          this.hotels = []
          this.requirements = {}
          this.$message.error(result.message || '查询失败，请稍后重试')
          return
        }
        const answer = result.data && result.data.answerDTOs
        const anArr = Array.isArray(answer) ? answer : []
        this.tips = anArr.map(item => item.aiMessage).flat().filter(item => item)
        this.hotels = anArr.map(item => item.hotels).flat()
        if (answer.length === 1) {
          this.actualSearchCondition = answer[0].actualSearchCondition || {}
          this.parsedSearchCondition = answer[0].parsedSearchCondition || {}
        }
        this.hotels.forEach(h => {
          let lowestPrice = h.roomRatePlans.reduce((curr, obj) => {
            if (!curr) {
              return obj.totalPrice
            }
            return obj.totalPrice < curr ? obj.totalPrice : curr
          }, 0)
          this.$set(h, 'lowestPrice', lowestPrice)
          h.roomRatePlans.forEach(r => {
            this.$set(r, 'policy', `${this.getMealDesc(r.mealCount)} · ${r.cancel === 'yes' ? '免费取消' : '不可取消'} ${r.instantConfirm === 1 ? '· 立即确认' : ''}`)
          })
          // if (this.actualSearchCondition.requiredTagNames && this.actualSearchCondition.requiredTagNames.length) {
          //   let hotelTags = Array.isArray(h.tagNames) ? h.tagNames : []
          //   let tags =  Array.from(new Set([...this.actualSearchCondition.requiredTagNames, ...hotelTags]))
          //   h.tagNames = tags.slice(0, 3)
          // } else {
          //   h.tagNames = Array.isArray(h.tagNames) ? h.tagNames.slice(0, 3) : []
          // }
          if (h.requiredTagNames && h.requiredTagNames.length) {
            let hotelTags = Array.isArray(h.tagNames) ? h.tagNames : []
            let tags =  Array.from(new Set([...h.requiredTagNames, ...hotelTags]))
            h.tagNames = tags.slice(0, 3)
          } else {
            h.tagNames = Array.isArray(h.tagNames) ? h.tagNames.slice(0, 3) : []
          }
        })
        this.requirements = defaults(this.actualSearchCondition.location, this.actualSearchCondition.checkInDate,
          this.actualSearchCondition.nightCount, this.actualSearchCondition.roomCount, this.actualSearchCondition.adultCount,
          this.actualSearchCondition.starRatingsDesc, this.actualSearchCondition.requiredTagNames || [],
          this.actualSearchCondition.minPricePerNight, this.actualSearchCondition.maxPricePerNight)
        this.notMappingRequirements = defaults(this.parsedSearchCondition.location, this.parsedSearchCondition.checkInDate,
          this.parsedSearchCondition.nightCount, this.parsedSearchCondition.roomCount, this.parsedSearchCondition.adultCount,
          this.parsedSearchCondition.starRatingsDesc, this.parsedSearchCondition.requiredTagNames || [],
          this.parsedSearchCondition.minPricePerNight, this.parsedSearchCondition.maxPricePerNight)
        let keys = Object.keys(this.requirements)
        for (let key of keys) {
          if (this.requirements[key]) {
            if (key === 'tags' && !this.requirements[key].length) {
              continue
            }
            this.$delete(this.notMappingRequirements, key)
          }
        }
        this.hasSearched = true
        this.$emit('search-state-change', true)
        this.$emit('search', {
          region: this.region,
          query: this.submittedQuery,
          requirements: Object.assign({}, this.requirements)
        })
      } catch (error) {
        this.$message.error('查询失败，请稍后重试')
      } finally {
        clearInterval(this.loadingTimer)
        this.loadingTimer = null
        this.isSearching = false
      }
    },
    getMealDesc (mealCount) {
      if (mealCount === 0) return '不含早'
      if (mealCount === 1) return '单早'
      if (mealCount === 2) return '双早'
      if (mealCount === 3) return '三早'
      if (mealCount === 4) return '四早'
      if (mealCount === 5) return '五早'
      if (mealCount === 6) return '六早'
      if (mealCount === 7) return '七早'
      if (mealCount === 8) return '八早'
    },
    getCheckout (checkIn, nightCount) {
      const [year, month, day] = checkIn.split('-').map(Number)
      const date = new Date(Date.UTC(year, month - 1, day))
      date.setUTCDate(date.getUTCDate() + nightCount)
      return date.toISOString().slice(0, 10)
    },
    toHotelDetail (hotel) {
      let obj = {
        room: hotel.roomCount,
        adult: hotel.adultCount,
        checkDates: [hotel.checkInDate, hotel.checkOutDate],
        checkIn: hotel.checkInDate,
        checkOut: hotel.checkOutDate,
        channelId: null,
        regions: [],
        stars: [0],
        price: 0,
        siteType: 0,
        districtName: null,
        bizZoneName: null,
        starRating: null,
        hotelId: hotel.hotelId,
        taxFeeAsCost: 1,
        showPackagePrice: false,
        website: true
      }
      localStorage.setItem('hotelDetailSearchForm', JSON.stringify(obj))
      window.open('/HotelDetail', '_blank')
    },
    toBookingDetail ({hotel, room}) {
      let loading = this.$loading({
        text: '努力加载中，请稍后.....',
        spinner: 'el-icon-loading',
        background: this.$finalData.LoadingBgColorAll
      })
      this.checkRateReq = {
        hotelId: hotel.hotelId,
        roomId: room.roomTypeId,
        rateId: room.rateId,
        checkIn: hotel.checkInDate,
        checkOut: hotel.checkOutDate,
        currency: this.$finalData.CNYCurrency,
        adults: hotel.adultCount,
        rooms: hotel.roomCount,
        totalNet: room.totalPrice + '',
        showPackagePrice: false,
        taxFeeAsCost: 1,
        rateKey: room.rateKey,
        pkgProductId: null,
        showPkgProductInfo: {}
      }
      this.bookingInfo = {
        room: hotel.roomCount,
        adult: hotel.adultCount,
        checkDates: [hotel.checkInDate, hotel.checkOutDate],
        checkIn: hotel.checkInDate,
        checkOut: hotel.checkOutDate,
        channelId: null,
        regions: [],
        stars: [0],
        price: 0,
        siteType: 0,
        districtName: null,
        bizZoneName: null,
        starRating: null,
        hotelId: hotel.hotelId,
        taxFeeAsCost: 1,
        showPackagePrice: false,
        website: true
      }
      this.currentRoom = {
        area: room.area,
        bedInfo: room.bedTypeDescription,
        floor: room.floor,
        maxOccupancy: room.maxOccupancy,
        nameCn: room.roomNameCn,
        nameEn: room.roomName,
        roomId: room.roomTypeId,
        windowType: room.windowType
      }
      let checkRateHeader = {
        accessToken: JSON.parse(localStorage.getItem('partnerInfo')).token
      }
      let vm = this
      vm.$http.post('/mini/checkRate', vm.checkRateReq, {headers: checkRateHeader}).then(resp => {
        let result = resp.data.data
        if (!result) {
          return this.$message.warning(resp.body.message)
        }
        if (!result.net) {
          vm.isShowReturn = true
          return
        } else {
          vm.checkRateInfo = result
          vm.hotelInfoBriefly = {
            nameCn: hotel.name,
            nameEn: hotel.nameEn,
            star: hotel.starRating,
            addressCn: hotel.address,
            addressEn: hotel.addressEn,
            hotelImages: hotel.imageUrl ? [hotel.imageUrl] : [],
            score: hotel.score,
            category: hotel.category,
            district: hotel.district,
            bedInfo: room.bedTypeDescription,
            roomNameCn: room.roomNameCn,
            roomNameEn: room.roomName,
            mealCount: room.breakfastCount
          }
          vm.upPrice = result.net - room.totalPrice
          if (vm.upPrice > 0) {
            vm.isShowContinue = true
          } else {
            vm.upPrice = 0
            vm.toDetail()
          }
        }
      }).catch(resp => {
        vm.$message.error('Error：' + resp.status + ' - ' + resp.statusText)
      }).finally(() => {
        loading.close()
      })
    },
    toDetail () {
      let req = {
        rateInfo: this.checkRateInfo,
        checkRateReq: this.checkRateReq,
        hotelInfo: this.hotelInfoBriefly,
        searchForm: this.bookingInfo,
        room: this.currentRoom
      }
      this.$router.push({
        name: 'bookingDetail',
        path: '/bookingDetail',
        params: req
      })
    }
  }
}
</script>

<style scoped>
.smart-hotel-search {
  width: calc(100% - 40px);
  max-width: 1180px;
  margin: 0 auto;
  padding: 10% 0 30px;
  font-family: '苹方', 'PingFang SC', sans-serif;
  color: #30394b;
  line-height: 1.5;
}

.smart-hotel-search.awaiting-search {
  padding-bottom: 0;
}

.smart-hotel-search :not(i) {
  font-family: '苹方', 'PingFang SC', sans-serif !important;
}

.smart-hotel-search * {
  box-sizing: border-box;
}

.search-panel {
  background: rgba(232, 233, 235, .92);
  border-radius: 8px;
  padding: 25px 20px 18px;
}

.query-examples {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 12px;
}

.query-examples > span {
  font-size: 12px;
  color: #737982;
  font-family: "苹方", serif;
}

.query-examples button {
  max-width: 180px;
  padding: 5px 12px;
  border: 1px solid rgba(255, 255, 255, .9);
  border-radius: 20px;
  background: rgba(255, 255, 255, .8);
  color: #566176;
  font-size: 11px;
  line-height: 16px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  cursor: pointer;
}

.query-examples button:hover {
  background: #fff;
  color: #2553fa;
}

.toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  margin-bottom: 17px;
  height: 40px;
}

.region-tabs {
  display: flex;
  gap: 15px;
}

.region-tabs button {
  padding: 12px 24px;
  border: 0;
  border-radius: 80px;
  color: #4a5565;
  background: rgba(240, 240, 241, .8);
  font-family: '苹方', serif;
  font-size: 13px;
  font-weight: normal;
  line-height: 1;
  cursor: pointer;
}

.region-tabs button:hover {
  background: rgba(240, 240, 241, .5);
}

.region-tabs .active, .region-tabs .active:hover {
  font-size: 14px;
  background: #1e50f2;
  color: #fff;
  font-weight: bolder;
}

.query-bar {
  display: flex;
  align-items: center;
  background: #fff;
  border-radius: 8px;
  min-height: 84px;
  padding: 12px 18px 12px 21px;
  gap: 12px;
}

.ai-icon {
  background: #2853ff;
  color: #fff;
  width: 26px;
  height: 26px;
  border-radius: 7px;
  text-align: center;
  line-height: 26px;
  font-size: 20px;
  flex-shrink: 0;
}

.query-field {
  display: flex;
  flex: 1;
  flex-direction: column;
  min-width: 0;
  gap: 7px;
}

.query-field span {
  color: #6c7890;
  font-size: 12px;
}

.query-input /deep/ .el-textarea__inner {
  border: 0;
  background: transparent;
  width: 100%;
  color: #273246;
  font-size: 14px;
  line-height: 21px;
  padding: 0;
  outline: none;
  border-radius: 0;
  resize: none;
  overflow-y: auto;
  overflow-wrap: break-word;
}

.query-input /deep/ .el-textarea__inner:focus {
  border-color: transparent;
}

.query-input /deep/ .el-textarea__inner::-webkit-scrollbar {
  width: 5px;
}

.query-input /deep/ .el-textarea__inner::-webkit-scrollbar-thumb {
  background: #c5cfe2;
  border-radius: 5px;
}

.query-input-meta {
  display: flex;
  justify-content: space-between;
  gap: 12px;
}

.query-input-meta span {
  color: #8993a5;
  font-size: 10px;
  line-height: 14px;
}

.clear-query-button {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 28px;
  height: 28px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: transparent;
  color: #b2bac8;
  cursor: pointer;
}

.clear-query-button:hover {
  color: #738098;
}

.clear-query-button:focus-visible {
  outline: 2px solid #2553fa;
  outline-offset: 2px;
}

.search-button {
  flex-shrink: 0;
  min-width: 145px;
  min-height: 60px;
  background: #2553fa;
  border: 0;
  border-radius: 8px;
  color: #fff;
  font-size: 18px;
  font-weight: 600;
  cursor: pointer;
  box-shadow: 0 6px 16px rgba(37, 83, 250, .25);
  align-items: center;
}

.search-button i {
  font-size: 24px;
  vertical-align: middle;
}

.search-button.is-searching {
  background: #6280f4;
  cursor: wait;
}

.loading-sparkle {
  display: inline-block;
  margin-right: 6px;
  font-size: 23px;
  vertical-align: middle;
}

.search-progress {
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 28px;
  margin-top: 14px;
  padding-left: 4px;
  color: #657ba7;
  font-size: 12px;
  line-height: 20px;
}

.progress-spinner {
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  border: 2px solid #b7c7ff;
  border-top-color: #5476ff;
  border-radius: 50%;
  animation: smart-search-spin .8s linear infinite;
}

.region-tabs button:disabled {
  cursor: wait;
}

@keyframes smart-search-spin {
  to {
    transform: rotate(360deg);
  }
}

@media (prefers-reduced-motion: reduce) {
  .progress-spinner {
    animation: none;
  }
}

.results-panel {
  margin-top: 20px;
  padding: 18px;
  background: rgba(255, 255, 255, .97);
  border-radius: 10px;
}

.results-heading {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 9px;
  margin: 0 2px 14px;
  min-height: 28px;
  font-size: 12px;
}

.results-heading > strong {
  font-size: 14px;
  line-height: 22px;
  font-weight: 600;
}

.result-count {
  color: #7e899e;
  white-space: nowrap;
}

.requirement-tags {
  display: flex;
  flex-wrap: wrap;
  //flex: 1;
  gap: 8px;
}

.requirement-tags span {
  border: 1px solid #cceee2;
  background: #edf9f5;
  color: #15936a;
  border-radius: 20px;
  padding: 5px 10px;
  font-size: 11px;
  line-height: 16px;
  overflow-wrap: anywhere;
  max-width: 100%;
}

.requirement-tags.not span {
  border: 1px solid #f4dbb5;
  background: #fff7e9;
  color: #cb7111;
  border-radius: 20px;
  padding: 5px 10px;
  font-size: 11px;
  line-height: 16px;
  overflow-wrap: anywhere;
  max-width: 100%;
}

.hotel-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 14px;
  align-items: stretch;
}

.empty-state {
  padding: 60px 15px;
  text-align: center;
  color: #738098;
}

.empty-state i {
  font-size: 32px;
}

.dialog-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 10px;
}

.primary-button {
  border: 1px solid #2553fa;
  border-radius: 5px;
  padding: 10px 18px;
  cursor: pointer;
  background: #2553fa;
  color: #fff;
}

.demo-note {
  font-size: 12px;
  line-height: 20px;
  color: #8993a5;
  margin: 0;
}

.booking-preview h3 {
  margin: 0 0 16px;
  color: #30394b;
}

.booking-preview p {
  line-height: 22px;
}

.booking-preview > strong {
  display: block;
  color: #fa572e;
  font-size: 24px;
  margin: 20px 0;
}

.booking-preview small {
  font-size: 12px;
  font-weight: normal;
}

button:focus-visible {
  outline: 2px solid #2553fa;
  outline-offset: 3px;
}

@media (max-width: 1100px) {
  .hotel-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .requirement-tags {
    flex-basis: 60%;
  }
}

@media (max-width: 650px) {
  .smart-hotel-search {
    width: calc(100% - 24px);
    padding-top: 24px;
  }

  .search-panel {
    padding: 16px 12px;
  }

  .toolbar {
    flex-wrap: wrap;
    gap: 12px;
    height: auto;
  }

  .query-bar {
    flex-wrap: wrap;
    padding: 14px 12px;
  }

  .search-button {
    width: 100%;
    min-height: 46px;
    font-size: 16px;
  }

  .hotel-grid {
    grid-template-columns: minmax(0, 1fr);
  }

  .results-panel {
    padding: 12px;
  }

  .requirement-tags {
    flex-basis: 100%;
    order: 1;
  }
}
</style>

<style>
.smart-hotel-booking-dialog .el-dialog__header {
  display: none;
}
.smart-hotel-booking-dialog :not(i) {
  font-family: '苹方', 'PingFang SC', sans-serif !important;
}
</style>



// WEBPACK FOOTER //
// src/pages/newPages/booking/components/smartHotelSearch/index.vue